import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { translateText } from '../services/translate';
import '../styles/AdminDashboard.css';
import '../styles/Partners.css';

const PRODUCTS_API = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/products/`;

const emptyForm = {
  name: '', name_en: '', name_ar: '',
  description: '', description_en: '', description_ar: '',
  price: '', whatsapp_number: '', image: null
};

const IconTrash = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const IconEdit = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);



export function AdminProductsContent({ onStatsChange }) {
  const { i18n } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [modal, setModal] = useState({ show: false, title: '', message: '', type: 'info', onConfirm: null });

  const isSuperAdmin = localStorage.getItem('is_superadmin') === 'true';
  const canManage = isSuperAdmin || localStorage.getItem('can_manage_products') === 'true';

  const token = () => localStorage.getItem('access_token');
  const authHeader = () => ({ Authorization: `Bearer ${token()}` });

  const closeModal = () => setModal(m => ({ ...m, show: false, onConfirm: null }));
  const notify = (type, title, message) => setModal({ show: true, type, title, message, onConfirm: null });
  const confirmModal = (title, message, onConfirm) =>
    setModal({ show: true, type: 'warning', title, message, onConfirm });

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(PRODUCTS_API, {
        headers: {
          'Accept-Language': i18n.language || 'fr'
        }
      });
      if (res.ok) setProducts(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '',
      name_en: p.name_en || '',
      name_ar: p.name_ar || '',
      description: p.description || '',
      description_en: p.description_en || '',
      description_ar: p.description_ar || '',
      price: p.price || '',
      whatsapp_number: p.whatsapp_number || '',
      image: null
    });
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); setForm(emptyForm); };

  const handleChange = (e) => {
    const { name, value, files, type } = e.target;
    if (type === 'file') {
      setForm(prev => ({ ...prev, [name]: files[0] || null }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const buildFD = () => {
    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('name_en', form.name_en);
    fd.append('name_ar', form.name_ar);
    fd.append('description', form.description);
    fd.append('description_en', form.description_en);
    fd.append('description_ar', form.description_ar);
    fd.append('price', form.price);
    fd.append('whatsapp_number', form.whatsapp_number);
    if (form.image) fd.append('image', form.image);
    return fd;
  };

  const handleAutoTranslate = async () => {
    if (!form.name && !form.description) {
      notify('warning', 'Champs vides', 'Veuillez remplir au moins le nom ou la description en français.');
      return;
    }

    setSubmitting(true);
    try {
      const translations = {};

      if (form.name) {
        translations.name_en = await translateText(form.name, 'fr', 'en');
        translations.name_ar = await translateText(form.name, 'fr', 'ar');
      }

      if (form.description) {
        translations.description_en = await translateText(form.description, 'fr', 'en');
        translations.description_ar = await translateText(form.description, 'fr', 'ar');
      }

      setForm(prev => ({ ...prev, ...translations }));
      notify('success', 'Traduction terminée', 'Les champs ont été traduits automatiquement.');
    } catch (e) {
      console.error(e);
      notify('error', 'Erreur', 'La traduction a échoué.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editing && !form.image) { notify('error', 'Image manquante', 'Veuillez sélectionner une image.'); return; }
    setSubmitting(true);
    try {
      const url = editing ? `${PRODUCTS_API}${editing.id}/` : PRODUCTS_API;
      const method = editing ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers: authHeader(), body: buildFD() });
      if (res.ok) {
        closeForm();
        fetchProducts();
        if (onStatsChange) onStatsChange();
        notify('success', editing ? 'Produit modifié' : 'Produit ajouté', 'Opération réussie.');
      } else {
        notify('error', 'Erreur', 'Impossible de sauvegarder le produit.');
      }
    } catch {
      notify('error', 'Erreur serveur', 'Connexion impossible.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    confirmModal('Supprimer le produit', 'Ce produit sera retiré du site. Confirmer ?', async () => {
      const res = await fetch(`${PRODUCTS_API}${id}/`, { method: 'DELETE', headers: authHeader() });
      if (res.ok) { fetchProducts(); if (onStatsChange) onStatsChange(); notify('success', 'Supprimé', 'Le produit a été supprimé.'); }
      else notify('error', 'Erreur', 'Impossible de supprimer.');
    });
  };

  return (
    <>
      {/* Modal */}
      {modal.show && (
        <div className="tiwizi-modal-overlay" onClick={closeModal}>
          <div className="tiwizi-modal-card tiwizi-details-modal" onClick={e => e.stopPropagation()}>
            <div className={`tiwizi-modal-icon ${modal.type === 'success' ? 'success' : modal.type === 'warning' ? 'warning' : 'error'}`}>
              {modal.type === 'success'
                ? <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                : <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              }
            </div>
            <h3>{modal.title}</h3>
            <p style={{ whiteSpace: 'pre-wrap' }}>{modal.message}</p>
            <div className="tiwizi-modal-actions">
              {modal.onConfirm ? (
                <>
                  <button className="tiwizi-btn-cancel" onClick={closeModal}>Annuler</button>
                  <button className="tiwizi-btn-confirm" onClick={() => { modal.onConfirm(); closeModal(); }}>Confirmer</button>
                </>
              ) : (
                <button className="tiwizi-btn-cancel" onClick={closeModal}>Fermer</button>
              )}
            </div>
          </div>
        </div>
      )}

      <section className="data-section">
        <div className="section-title-group">
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
            Produits de l'association
            <span className="count-badge dark">{products.length}</span>
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="projets-view-toggle">
              <button type="button" className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Vue Liste">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              </button>
              <button type="button" className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Vue Grille">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                </svg>
              </button>
            </div>
            {canManage && (
              <button onClick={openCreate} className="btn-add-partner">+ Ajouter un produit</button>
            )}
          </div>
        </div>

        {/* Add / Edit form */}
        {showForm && (
          <div className="add-partner-form-container">
            <h4 style={{ marginTop: 0, marginBottom: '20px' }}>
              {editing ? 'Modifier le produit' : 'Nouveau produit'}
            </h4>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-column">
                  <div className="form-field">
                    <label>Nom du produit (FR) <span style={{ color: '#e74c3c' }}>*</span></label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Ex: Huile d'argan" required />
                  </div>
                  <div className="form-field">
                    <label>Nom du produit (EN)</label>
                    <input type="text" name="name_en" value={form.name_en} onChange={handleChange} placeholder="Ex: Argan oil" />
                  </div>
                  <div className="form-field">
                    <label>Nom du produit (AR)</label>
                    <input type="text" name="name_ar" value={form.name_ar} onChange={handleChange} placeholder="Ex: زيت الأركان" style={{ direction: 'rtl' }} />
                  </div>
                  <div className="form-field">
                    <label>Description (FR) <span style={{ color: '#e74c3c' }}>*</span></label>
                    <textarea name="description" value={form.description} onChange={handleChange} placeholder="Description du produit..." rows={3} required />
                  </div>
                  <div className="form-field">
                    <label>Description (EN)</label>
                    <textarea name="description_en" value={form.description_en} onChange={handleChange} placeholder="Product description..." rows={3} />
                  </div>
                  <div className="form-field">
                    <label>Description (AR)</label>
                    <textarea name="description_ar" value={form.description_ar} onChange={handleChange} placeholder="وصف المنتج..." rows={3} style={{ direction: 'rtl' }} />
                  </div>
                  <div className="form-field">
                    <label>Prix (optionnel)</label>
                    <input type="text" name="price" value={form.price} onChange={handleChange} placeholder="Ex: 50 MAD" />
                  </div>
                  <div className="form-field">
                    <label>Numéro WhatsApp <span style={{ color: '#e74c3c' }}>*</span></label>
                    <input type="text" name="whatsapp_number" value={form.whatsapp_number} onChange={handleChange} placeholder="Ex: 212600000000" required />
                  </div>
                </div>
                <div className="form-column">
                  <div className="form-field">
                    <label>Image {!editing && <span style={{ color: '#e74c3c' }}>*</span>}</label>
                    <label className={`upload-zone ${form.image ? 'has-file' : ''}`}>
                      {form.image ? (
                        <>
                          <img src={URL.createObjectURL(form.image)} alt="Prévisu" />
                          <span>{form.image.name}</span>
                        </>
                      ) : editing && editing.image ? (
                        <>
                          <img src={editing.image} alt="Actuelle" />
                          <span style={{ fontSize: '0.8rem', color: '#999' }}>Cliquer pour changer</span>
                        </>
                      ) : (
                        <>
                          <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#f5c50a" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                          <span className="upload-text">Cliquer pour choisir une image</span>
                        </>
                      )}
                      <input type="file" accept="image/*" name="image" onChange={handleChange} />
                    </label>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #eee' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#666' }}>Outils de traduction</span>
                  <button
                    type="button"
                    onClick={handleAutoTranslate}
                    className="btn-translate-auto"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#f5c50a',
                      color: '#1a1a1a', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.85rem'
                    }}
                    disabled={submitting}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" /><polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" /><line x1="4" y1="4" x2="9" y2="9" />
                    </svg>
                    {submitting ? 'Traduction...' : 'Traduire automatiquement'}
                  </button>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#888', margin: 0 }}>
                  Remplit automatiquement les champs Anglais et Arabe à partir du texte Français ci-dessus.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
                <button type="button" onClick={closeForm} style={{ flex: 1, padding: '14px', borderRadius: '12px', border: '1px solid #ddd', background: '#fff', fontWeight: '700', cursor: 'pointer' }}>
                  Annuler
                </button>
                <button type="submit" className="btn-submit-partner" style={{ flex: 2 }} disabled={submitting}>
                  {submitting ? 'Enregistrement...' : editing ? 'Enregistrer les modifications' : 'Ajouter le produit'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Table */}
        {viewMode === 'list' && (
          <div className="admin-table-container">
            {loading ? <p className="loading-text">Chargement...</p> : (
              <table className="admin-table">
                <thead>
                  <tr><th>Image</th><th>Nom</th><th>Prix</th><th>WhatsApp</th><th>Date</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {products.length === 0 ? (
                    <tr><td colSpan="6" className="empty-table">Aucun produit pour le moment.</td></tr>
                  ) : products.map(p => (
                    <tr key={p.id}>
                      <td>{p.image ? <img src={p.image} alt={p.name} className="table-logo-preview" /> : '—'}</td>
                      <td><strong>{p.name}</strong></td>
                      <td>{p.price || '—'}</td>
                      <td>{p.whatsapp_number}</td>
                      <td>{new Date(p.created_at).toLocaleDateString('fr-FR')}</td>
                      <td>
                        {canManage ? (
                          <div className="action-btns">
                            <button className="icon-btn" title="Modifier" onClick={() => openEdit(p)}><IconEdit /></button>
                            <button className="icon-btn" title="Supprimer" style={{ color: '#e74c3c' }} onClick={() => handleDelete(p.id)}><IconTrash /></button>
                          </div>
                        ) : (
                          <span title="Accès restreint" style={{ color: '#cbd5e1', display: 'inline-flex' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Grid */}
        {viewMode === 'grid' && (
          loading ? <p className="loading-text">Chargement...</p> : (
            <div className="products-grid-container">
              {products.length === 0 ? (
                <p className="empty-table" style={{ gridColumn: '1/-1' }}>Aucun produit pour le moment.</p>
              ) : products.map(p => (
                <div key={p.id} style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ height: '160px', background: '#f8f9fa', overflow: 'hidden', position: 'relative' }}>
                    {p.image
                      ? <img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                      </div>
                    }
                  </div>
                  <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <strong style={{ fontSize: '0.95rem', color: '#1a1a1a' }}>{p.name}</strong>
                    {p.price && <span style={{ fontSize: '0.85rem', color: '#f5c50a', fontWeight: '700' }}>{p.price}</span>}
                    <span style={{ fontSize: '0.78rem', color: '#888' }}>{p.whatsapp_number}</span>
                    <span style={{ fontSize: '0.75rem', color: '#bbb', marginTop: 'auto' }}>{new Date(p.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                  {canManage && (
                    <div style={{ padding: '10px 16px', borderTop: '1px solid #f5f5f5', display: 'flex', gap: '8px' }}>
                      <button className="icon-btn" title="Modifier" onClick={() => openEdit(p)} style={{ flex: 1, justifyContent: 'center' }}><IconEdit /></button>
                      <button className="icon-btn" title="Supprimer" style={{ flex: 1, justifyContent: 'center', color: '#e74c3c' }} onClick={() => handleDelete(p.id)}><IconTrash /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </section>
    </>
  );
}

export default AdminProductsContent;
